import type { AxiosInstance } from 'axios';
import type { ExtractionQuestion } from '../types/extracted-answers.api.js';
import { type ProjectClips } from '../types/project.clips.js';
import type { Projects } from './interfaces/index.js';
import type { DownloadInsightsSummary, ExportProjectGoodQuotes, ExportProjectTaggedMoments } from './interfaces/projects.js';
import type * as SegmentsApi from './interfaces/projects.segments.js';
import { transformConferenceTagSummaryFeedback, transformConferenceTagSummaryItem, transformDates, transformTagSummary } from './transformers/index.js';
import qs from './utils/query-string.js';

export * as entities from './projects.entities.js';

export class ProjectsClient {
  constructor(private authenticatedClient: AxiosInstance) { }

  createProject = (data: Projects.CreateProject.Request) => {
    return this.authenticatedClient.post<Projects.CreateProject.Response>('/projects', data)
      .then(res => res.data);
  };

  fetchProjects = (data: Projects.FetchProjects.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjects.Response>('/projects', {
      params: data,
    })
      .then(res => res.data);
  };

  fetchProject = (data: Projects.FetchProject.Request) => {
    return this.authenticatedClient.get<Projects.FetchProject.Response>(`/projects/${data.projectId}`)
      .then(res => res.data);
  };

  getProjectParent = (data: Projects.GetProjectParent.Request) => {
    return this.authenticatedClient.get<Projects.GetProjectParent.Response>(`/projects/parents/${data.projectParentId}`)
      .then(res => res.data);
  };

  fetchProjectFiles = (data: Projects.FetchProjectFiles.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectFiles.Response>(`/projects/${data.projectId}/files`, { transformResponse: transformDates })
      .then(res => res.data);
  };

  fetchProjectTags = (data: Projects.FetchProjectTags.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectTags.Response>(`/projects/${data.projectId}/tags`)
      .then(res => ({
        tags: res.data.tags,
      }));
  };

  updateProjectTags = (data: Projects.UpdateProjectTags.Request) => {
    return this.authenticatedClient.put<Projects.UpdateProjectTags.Response>(`/projects/${data.projectId}/tags`, {
      ...data,
    })
      .then(res => ({
        items: res.data.items,
      }));
  };

  fetchProjectTerms = (data: Projects.FetchProjectTerms.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectTerms.Response>(`/projects/${data.projectId}/terms`)
      .then(res => ({
        terms: res.data.terms,
      }));
  };

  fetchProjectTermHighlights = (data: Projects.FetchProjectTermHighlights.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectTermHighlights.Response>(`/projects/${data.projectId}/terms/highlights`, {
      params: {
        term: data.term,
        searchAfter: data.searchAfter,
      },
    })
      .then(res => ({
        highlights: res.data.highlights,
        searchAfter: res.data.searchAfter,
        total: res.data.total,
      }));
  };

  updateProjectDetails = ({ projectId, ...data }: Projects.UpdateProjectDetails.Request) => {
    return this.authenticatedClient.patch<Projects.UpdateProjectDetails.Response>(`/projects/${projectId}`, {
      ...data,
    })
      .then(res => ({
        project: res.data.project,
      }));
  };

  updateProjectStatus = ({ projectId, ...data }: Projects.UpdateProjectStatus.Request): Promise<void> => {
    return this.authenticatedClient.patch<Projects.UpdateProjectStatus.Response>(`/projects/${projectId}/status`, {
      ...data,
    })
      .then(() => undefined);
  };

  fetchProjectNoteItems = (data: Projects.FetchProjectNoteItems.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectNoteItems.Response>(`/projects/${data.projectId}/notes`)
      .then(res => ({
        items: res.data.items,
      }));
  };

  updateProjectNoteItems = (data: Projects.UpdateProjectNoteItems.Request) => {
    return this.authenticatedClient.put<Projects.UpdateProjectNoteItems.Response>(`/projects/${data.projectId}/notes`, {
      ...data,
    })
      .then(res => ({
        items: res.data.items,
      }));
  };

  updateProjectTag = (data: Projects.UpdateProjectTag.Request) => {
    return this.authenticatedClient.patch<Projects.UpdateProjectTag.Response>(`/projects/${data.projectId}/tags/${data.tagId}`, data.updates)
      .then(res => ({
        tag: res.data.tag,
      }));
  };

  updateProjectTagBannerImage = (data: Projects.UpdateTagBannerImage.Request) => {
    const formData = new FormData();
    formData.append('banner', data.file);

    return this.authenticatedClient.post<Projects.UpdateTagBannerImage.Response>(
      `/projects/${data.projectId}/tags/${data.tagId}/banner-image`,
      formData,
    ).then(res => res.data);
  };

  updateProjectTagPrompts = (data: Projects.UpdateProjectTagPrompts.Request) => {
    const { projectId, projectTagId, ...body } = data;
    return this.authenticatedClient.patch<Projects.UpdateProjectTagPrompts.Response>(
      `/projects/${projectId}/conferences/tags/${projectTagId}/prompts`,
      body,
    ).then(res => res.data);
  };

  getTagExtractionQuestions = ({ projectId, tagId }: GetTagExtractionQuestions.Request) => {
    return this.authenticatedClient.get<GetTagExtractionQuestions.Response>(
      `/projects/${projectId}/conferences/tags/${tagId}/extraction-questions`,
    ).then(res => res.data);
  };

  updateTagExtractionQuestions = ({ projectId, tagId, questions }: UpdateTagExtractionQuestions.Request) => {
    return this.authenticatedClient.put<UpdateTagExtractionQuestions.Response>(
      `/projects/${projectId}/conferences/tags/${tagId}/extraction-questions`,
      { questions },
    ).then(res => res.data);
  };

  downloadInsightsSummary = (data: DownloadInsightsSummary.Request) => {
    const params = {
      export: true,
      format: data.format || `pptx`,
    };

    return this.authenticatedClient.get<Blob>(`/projects/${data.projectId}/insights/export?${qs.stringify(params)}`, { responseType: 'blob' })
      .then<DownloadInsightsSummary.Response>(res => ({
        file: res.data,
        name: `Early Insights Summary.${params.format}`,
      }));
  };

  exportProjectTaggedMoments = (data: ExportProjectTaggedMoments.Request) => {
    return this.authenticatedClient.get<ExportProjectTaggedMoments.Response>(`/projects/${data.projectId}/conferences/tagged-moments/export`);
  };

  exportProjectGoodQuotes = (data: ExportProjectGoodQuotes.Request) => {
    return this.authenticatedClient.get<ExportProjectGoodQuotes.Response>(
      `/projects/${data.projectId}/conferences/good-quotes/export?${qs.stringify({ format: data.format })}`
    );
  };

  deleteProjectTag = (data: Projects.DeleteProjectTag.Request) => {
    return this.authenticatedClient.delete<Projects.DeleteProjectTag.Response>(`/projects/${data.projectId}/tags/${data.tagId}`)
      .then(res => ({
        rebalancedOrdinals: res.data.rebalancedOrdinals,
      }));
  };

  createProjectTag = ({ projectId, ...data }: Projects.CreateProjectTag.Request) => {
    return this.authenticatedClient.post<Projects.CreateProjectTag.Response>(`/projects/${projectId}/tags`, data)
      .then(res => ({
        tag: res.data.tag,
        rebalancedOrdinals: res.data.rebalancedOrdinals,
      }));
  };

  getPatientJourneys = (projectId: number) => {
    return this.authenticatedClient.get<Projects.GetPatientJourneys.Response>(`/projects/${projectId}/patient-journey/journeys`)
      .then(res => res.data);
  };

  createPatientJourney = ({ projectId, ...data }: Projects.CreatePatientJourney.Request) => {
    return this.authenticatedClient.post<Projects.CreatePatientJourney.Response>(`/projects/${projectId}/patient-journey/journeys`, data)
      .then(res => res.data);
  };

  getPatientJourneyTagAssociations = ({ projectId, journeyId }: Projects.GetPatientJourneyTagAssociations.Request) => {
    return this.authenticatedClient.get<Projects.GetPatientJourneyTagAssociations.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}/tags`)
      .then(res => res.data);
  };

  getPatientJourneyById = ({ projectId, journeyId }: Projects.GetPatientJourneyById.Request) => {
    return this.authenticatedClient.get<Projects.GetPatientJourneyById.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}`)
      .then(res => res.data);
  };

  updatePatientJourneyMetadata = ({ projectId, journeyId, ...data }: Projects.UpdatePatientJourneyMetadata.Request) => {
    return this.authenticatedClient.patch<Projects.UpdatePatientJourneyMetadata.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}`, data)
      .then(res => res.data);
  };

  pinGlobalPatientJourney = ({ projectId, journeyId }: Projects.PinGlobalPatientJourney.Request) => {
    return this.authenticatedClient.patch<Projects.PinGlobalPatientJourney.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}/pin-global`)
      .then(res => res.data);
  };

  publishPatientJourney = ({ projectId, journeyId }: Projects.PublishPatientJourney.Request) => {
    return this.authenticatedClient.patch<Projects.PublishPatientJourney.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}/publish`)
      .then(res => res.data);
  };

  unpublishPatientJourney = ({ projectId, journeyId }: Projects.UnpublishPatientJourney.Request) => {
    return this.authenticatedClient.patch<Projects.UnpublishPatientJourney.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}/unpublish`)
      .then(res => res.data);
  };

  deletePatientJourney = ({ projectId, journeyId }: Projects.DeletePatientJourney.Request) => {
    return this.authenticatedClient.delete<Projects.DeletePatientJourney.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}`)
      .then(res => res.data);
  };

  replacePatientJourneyTagAssociations = ({ projectId, journeyId, ...data }: Projects.ReplacePatientJourneyTagAssociations.Request) => {
    return this.authenticatedClient.put<Projects.ReplacePatientJourneyTagAssociations.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}/tags`, data)
      .then(res => res.data);
  };

  getPatientJourneyGrid = ({ projectId, journeyId }: Projects.GetPatientJourneyGrid.Request) => {
    return this.authenticatedClient.get<Projects.GetPatientJourneyGrid.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}/grid`)
      .then(res => res.data);
  };

  updatePatientJourneyGrid = ({ projectId, journeyId, ...data }: Projects.UpdatePatientJourneyGrid.Request) => {
    return this.authenticatedClient.put<Projects.UpdatePatientJourneyGrid.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}/grid`, data)
      .then(res => res.data);
  };

  getPatientJourneyPhase = ({ projectId, journeyId, stageId }: Projects.GetPatientJourneyPhase.Request) => {
    return this.authenticatedClient.get<Projects.GetPatientJourneyPhase.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}/phases/${stageId}`)
      .then(res => res.data);
  };

  updatePatientJourneyPhase = ({ projectId, journeyId, stageId, ...data }: Projects.UpdatePatientJourneyPhase.Request) => {
    return this.authenticatedClient.put<Projects.UpdatePatientJourneyPhase.Response>(`/projects/${projectId}/patient-journey/journeys/${journeyId}/phases/${stageId}`, data)
      .then(res => res.data);
  };

  getMediaLibrary = ({ projectId }: Projects.GetMediaLibrary.Request) => {
    return this.authenticatedClient.get<Projects.GetMediaLibrary.Response>(`/projects/${projectId}/media-library`)
      .then(res => res.data);
  };

  updateMediaLibrary = ({ projectId, ...data }: Projects.UpdateMediaLibrary.Request) => {
    return this.authenticatedClient.put<Projects.UpdateMediaLibrary.Response>(`/projects/${projectId}/media-library`, data)
      .then(res => res.data);
  };

  reorderProjectTags = ({ projectId, ordinals }: Projects.ReorderProjectTags.Request) => {
    return this.authenticatedClient.patch<Projects.ReorderProjectTags.Response>(`/projects/${projectId}/tags/reorder`, { ordinals })
      .then(res => ({
        rebalancedOrdinals: res.data.rebalancedOrdinals,
      }));
  };

  getProjectWidgets = (data: Projects.GetProjectWidgets.Request) => {
    return this.authenticatedClient.get<Projects.GetProjectWidgets.Response>(`/projects/${data.projectId}/widgets`)
      .then(res => res.data);
  };

  createProjectWidget = ({ projectId, ...data }: Projects.CreateProjectWidget.Request) => {
    return this.authenticatedClient.post<Projects.CreateProjectWidget.Response>(`/projects/${projectId}/widgets`, data)
      .then(res => res.data);
  };

  updateProjectWidget = (data: Projects.UpdateProjectWidget.Request) => {
    return this.authenticatedClient.put<Projects.UpdateProjectWidget.Response>(`/projects/${data.projectId}/widgets/${data.widgetId}`, data.payload)
      .then(res => res.data);
  };

  deleteProjectWidget = (data: Projects.DeleteProjectWidget.Request) => {
    return this.authenticatedClient.delete<Projects.DeleteProjectWidget.Response>(`/projects/${data.projectId}/widgets/${data.widgetId}`)
      .then(res => res.data);
  };

  fetchProjectClips = ({ projectId, ...params }: FetchProjectClips.Request) => {
    return this.authenticatedClient.get<FetchProjectClips.Response>(`/projects/${projectId}/clips?${qs.stringify(params)}`)
      .then(res => res.data);
  };

  getProjectTranscriptGroupTags = (data: GetProjectTranscriptGroupTags.Request) => {
    return this.authenticatedClient.get<GetProjectTranscriptGroupTags.Response>(`/projects/${data.projectId}/transcript-group-tags`)
      .then(res => res.data);
  };

  getProjectTranscripts = (data: GetProjectTranscripts.Request) => {
    return this.authenticatedClient.get<GetProjectTranscripts.Response>(`/projects/${data.projectId}/transcripts`)
      .then(res => res.data);
  };

  getTagSummaries = (data: Projects.GetProjectConferenceTagSummaries.Request) => {
    return this.authenticatedClient.get<Projects.GetProjectConferenceTagSummaries.Response<string>>(`/projects/${data.projectId}/conferences/tags/summaries`)
      .then(res => {
        return {
          ...res.data,
          items: res.data.items.map(transformConferenceTagSummaryItem),
        };
      });
  };


  getTagSummaryCitations = ({ projectId, tagSummaryId }: Projects.GetTagSummaryCitations.Request) => {
    return this.authenticatedClient.get<Projects.GetTagSummaryCitations.Response>(`/projects/${projectId}/conferences/tags/summaries/${tagSummaryId}/citations`)
      .then(res => res.data);
  };

  getTagSummaryVersions = ({ projectId, tagSummaryId }: Projects.GetTagSummaryVersions.Request) => {
    return this.authenticatedClient.get<Projects.GetTagSummaryVersions.Response<string>>(`/projects/${projectId}/conferences/tags/summaries/${tagSummaryId}/versions`)
      .then(res => {
        return {
          ...res.data,
          items: res.data.items.map(transformTagSummary),
        };
      });
  };

  generateTagSummaries = ({ projectId, ...data }: Projects.GenerateProjectConferenceTagSummaries.Request) => {
    return this.authenticatedClient.post<Projects.GenerateProjectConferenceTagSummaries.Response>(`/projects/${projectId}/conferences/tags/summaries/generate`, {
      tagIds: data.tagIds,
    });
  };

  updateTagSummaryRating = ({ projectId, tagSummaryId, ...data }: Projects.UpdateTagSummaryRating.Request): Promise<Projects.UpdateTagSummaryRating.Response> => {
    return this.authenticatedClient.patch(`/projects/${projectId}/conferences/tags/summaries/${tagSummaryId}/rating`, data).then(x => x.data);
  };

  updateTagSummaryNotes = ({ projectId, tagSummaryId, ...data }: Projects.UpdateTagSummaryNotes.Request): Promise<Projects.UpdateTagSummaryNotes.Response> => {
    return this.authenticatedClient.patch(`/projects/${projectId}/conferences/tags/summaries/${tagSummaryId}/notes`, data).then(x => x.data);
  };

  getTagSummaryNotes = ({ projectId, tagSummaryId }: Projects.GetTagSummaryNotes.Request): Promise<Projects.GetTagSummaryNotes.Response> => {
    return this.authenticatedClient.get(`/projects/${projectId}/conferences/tags/summaries/${tagSummaryId}/notes`).then(x => x.data);
  };

  getTagSummaryFeedback = ({ projectId, tagSummaryId }: Projects.GetTagSummaryFeedback.Request) => {
    return this.authenticatedClient.get<Projects.GetTagSummaryFeedback.Response>(`/projects/${projectId}/conferences/tags/summaries/${tagSummaryId}/feedback`).then(x => x.data);
  };

  updateTagSummaryFeedback = ({ projectId, tagSummaryId, ...data }: Projects.UpdateTagSummaryFeedback.Request): Promise<Projects.UpdateTagSummaryFeedback.Response> => {
    return this.authenticatedClient.patch(`/projects/${projectId}/conferences/tags/summaries/${tagSummaryId}/feedback`, data).then(x => x.data);
  };

  getTagSummaryFeedbackSummary = ({ projectId, tagSummaryId }: Projects.GetTagSummaryFeedbackSummary.Request): Promise<Projects.GetTagSummaryFeedbackSummary.Response> => {
    return this.authenticatedClient.get<Projects.GetTagSummaryFeedbackSummary.Response<string>>(`/projects/${projectId}/conferences/tags/summaries/${tagSummaryId}/feedback-summary`)
      .then(res => {
        return {
          ...res.data,
          items: res.data.items.map(transformConferenceTagSummaryFeedback),
        };
      });
  };

  fetchAllSurveyResponses = ({ projectId, ...query }: Projects.FetchMultiSurveyResponses.Request) => {
    return this.authenticatedClient.get<Projects.FetchMultiSurveyResponses.Response>(`/projects/${projectId}/surveys/all-responses?${qs.stringify(query)}`)
      .then(res => res.data);
  };

  fetchProjectSurveys = (data: Projects.FetchProjectSurveys.Request) => {
    return this.authenticatedClient.get<Projects.FetchProjectSurveys.Response>(`/projects/${data.projectId}/surveys`)
      .then(res => res.data);
  };

  // Plain "Project Segments" CRUD (apps/chat-api's routers/v1/projects/segments.ts).
  // Distinct from SegmentsClient (`segments.ts`), which targets the unrelated flat
  // `/segments` segments_v2 endpoints.

  fetchProjectSegments = (data: SegmentsApi.GetSegmentsByProjectId.Request) => {
    return this.authenticatedClient.get<SegmentsApi.GetSegmentsByProjectId.Response>(`/projects/${data.projectId}/segments`, { transformResponse: transformDates })
      .then(res => res.data);
  };

  fetchProjectSegmentById = (data: SegmentsApi.FetchSegmentById.Request) => {
    return this.authenticatedClient.get<SegmentsApi.FetchSegmentById.Response>(`/projects/${data.projectId}/segments/${data.segmentId}`, { transformResponse: transformDates })
      .then(res => res.data);
  };

  createProjectSegment = (data: SegmentsApi.CreateSegment.Request) => {
    return this.authenticatedClient.post<SegmentsApi.CreateSegment.Response>(`/projects/${data.projectId}/segments`, data.payload, { transformResponse: transformDates })
      .then(res => res.data);
  };

  updateProjectSegmentMetadata = (data: SegmentsApi.UpdateSegmentMetadata.Request) => {
    return this.authenticatedClient.put<SegmentsApi.UpdateSegmentMetadata.Response>(`/projects/${data.projectId}/segments/${data.segmentId}/metadata`, data.payload)
      .then(res => res.data);
  };

  updateProjectSegmentDescription = (data: SegmentsApi.UpdateSegmentDescription.Request) => {
    return this.authenticatedClient.put<SegmentsApi.UpdateSegmentDescription.Response>(`/projects/${data.projectId}/segments/${data.segmentId}/description`, data.payload)
      .then(res => res.data);
  };

  renameProjectSegment = (data: SegmentsApi.UpdateSegmentName.Request) => {
    return this.authenticatedClient.put<SegmentsApi.UpdateSegmentName.Response>(`/projects/${data.projectId}/segments/${data.segmentId}/name`, data.payload)
      .then(res => res.data);
  };

  publishProjectSegment = (data: SegmentsApi.PublishSegment.Request) => {
    return this.authenticatedClient.put<SegmentsApi.PublishSegment.Response>(`/projects/${data.projectId}/segments/${data.segmentId}/publish`)
      .then(res => res.data);
  };

  unpublishProjectSegment = (data: SegmentsApi.UnpublishSegment.Request) => {
    return this.authenticatedClient.put<SegmentsApi.UnpublishSegment.Response>(`/projects/${data.projectId}/segments/${data.segmentId}/unpublish`)
      .then(res => res.data);
  };

  deleteProjectSegment = (data: SegmentsApi.DeleteSegment.Request) => {
    return this.authenticatedClient.delete<SegmentsApi.DeleteSegment.Response>(`/projects/${data.projectId}/segments/${data.segmentId}`)
      .then(res => res.data);
  };

  updateProjectSegmentIcon = (data: SegmentsApi.UpdateSegmentIcon.Request) => {
    const formData = new FormData();
    formData.append('icon', data.file);

    return this.authenticatedClient.post<SegmentsApi.UpdateSegmentIcon.Response>(`/projects/${data.projectId}/segments/${data.segmentId}/icon`, formData)
      .then(res => res.data);
  };

  deleteProjectSegmentIcon = (data: SegmentsApi.DeleteSegmentIcon.Request) => {
    return this.authenticatedClient.delete<SegmentsApi.DeleteSegmentIcon.Response>(`/projects/${data.projectId}/segments/${data.segmentId}/icon`)
      .then(res => res.data);
  };

  updateProjectSegmentPersona = (data: SegmentsApi.UpdateSegmentPersona.Request) => {
    return this.authenticatedClient.put<SegmentsApi.UpdateSegmentPersona.Response>(`/projects/${data.projectId}/segments/${data.segmentId}/persona`, data.payload)
      .then(res => res.data);
  };

  updateProjectSegmentChatConfig = (data: SegmentsApi.UpdateSegmentChatConfig.Request) => {
    return this.authenticatedClient.put<SegmentsApi.UpdateSegmentChatConfig.Response>(`/projects/${data.projectId}/segments/${data.segmentId}/chat-config`, data.payload)
      .then(res => res.data);
  };

  updateProjectSegmentTtsSettings = (data: SegmentsApi.UpdateSegmentTtsSettings.Request) => {
    return this.authenticatedClient.put<SegmentsApi.UpdateSegmentTtsSettings.Response>(`/projects/${data.projectId}/segments/${data.segmentId}/tts-settings`, data.payload)
      .then(res => res.data);
  };

  updateProjectSegmentDashboard = (data: SegmentsApi.UpdateSegmentDashboard.Request) => {
    return this.authenticatedClient.put<SegmentsApi.UpdateSegmentDashboard.Response>(`/projects/${data.projectId}/segments/${data.segmentId}/dashboard`, data.payload)
      .then(res => res.data);
  };

}

namespace FetchProjectClips {
  export type Request = {
    groupTagIds?: number[];
    keywords?: string[];
    pageIndex?: number;
    pageSize?: number;
    sort?: string;
    sortDesc?: boolean;
    speakerRole?: string;
    tagIds?: number[];
    transcriptIds?: number[];
  } & IProjectId;

  export type Response = {
    items: ProjectClips.Item[];
    total: number;
  };
}

namespace GetProjectTranscriptGroupTags {
  export type Request = IProjectId;

  export type Response = {
    tags: {
      id: number;
      name: string;
    }[];
  };
}

namespace GetProjectTranscripts {
  export type Request = IProjectId;

  export type Response = {
    transcripts: {
      id: number;
      name: string;
    }[];
  };
}

namespace GetTagExtractionQuestions {
  export type Request = { projectId: number; tagId: number };

  export type Response = {
    items: Array<{
      id: number;
      identifier: string;
      question: ExtractionQuestion;
      ordinal: number;
    }>;
  };
}

namespace UpdateTagExtractionQuestions {
  export type Request = {
    projectId: number;
    tagId: number;
    questions: Array<{
      identifier: string;
      question: ExtractionQuestion;
      ordinal: number;
    }>;
  };

  export type Response = {
    items: Array<{
      id: number;
      identifier: string;
      question: ExtractionQuestion;
      ordinal: number;
    }>;
  };
}